// ParticleSystem.js - Enhanced particle system with better mouse interaction
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';

const ParticleSystem = ({ particleCount = 50, mouseInfluence = 25, containerRef }) => {
  const fallbackRef = useRef(null);
  const actualContainerRef = containerRef || fallbackRef;
  const [particles, setParticles] = useState([]);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef(null);
  const particlesRef = useRef([]);

  // Initialize particles with random properties
  useEffect(() => {
    const newParticles = Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      size: Math.random() * 4 + 2,
      x: Math.random() * 100,
      y: Math.random() * 100,
      baseX: Math.random() * 100,
      baseY: Math.random() * 100,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 10,
      vx: 0,
      vy: 0,
    }));
    setParticles(newParticles);
    particlesRef.current = newParticles;
  }, [particleCount]);

  // Update particle positions based on mouse
  const updateParticles = useCallback(() => {
    const container = actualContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = mousePositionRef.current.x;
    const mouseY = mousePositionRef.current.y;

    particlesRef.current = particlesRef.current.map(particle => {
      // Calculate distance from mouse
      const dx = (particle.x / 100 * rect.width) - mouseX;
      const dy = (particle.y / 100 * rect.height) - mouseY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const maxDistance = 250;

      if (distance < maxDistance) {
        // Repel particles from mouse
        const force = (1 - distance / maxDistance) * mouseInfluence;
        const angle = Math.atan2(dy, dx);
        
        particle.vx += Math.cos(angle) * force * 0.002;
        particle.vy += Math.sin(angle) * force * 0.002;
      }

      // Apply velocity
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Apply stronger friction
      particle.vx *= 0.7;
      particle.vy *= 0.7;

      // Return to base position slowly
      const returnForce = 0.0005; // Reduced from 0.01
      particle.vx += (particle.baseX - particle.x) * returnForce;
      particle.vy += (particle.baseY - particle.y) * returnForce;

      // Clamp velocity to prevent crazy movement
      const maxVelocity = 2;
      particle.vx = Math.max(-maxVelocity, Math.min(maxVelocity, particle.vx));
      particle.vy = Math.max(-maxVelocity, Math.min(maxVelocity, particle.vy));

      // Keep particles within bounds
      if (particle.x < -5) particle.x = 105;
      if (particle.x > 105) particle.x = -5;
      if (particle.y < -5) particle.y = 105;
      if (particle.y > 105) particle.y = -5;

      return particle;
    });

    setParticles([...particlesRef.current]);
  }, [actualContainerRef, mouseInfluence]);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      const container = actualContainerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      mousePositionRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    // Animation loop
    const animate = () => {
      updateParticles();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    document.addEventListener('mousemove', handleMouseMove);
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [updateParticles]);

  return (
    <div 
      ref={actualContainerRef === containerRef ? null : fallbackRef} 
      className="particle-system-container"
    >
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="floating-particle floating-particle-interactive"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            transform: `translate(-50%, -50%)`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            ease: "easeInOut",
            delay: particle.delay,
          }}
        />
      ))}
    </div>
  );
};

export default ParticleSystem;